import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Save } from "lucide-react";

const CONTENT_GROUPS = [
  {
    label: "Hero (Accueil)",
    keys: [
      { key: "hero.subtitle", label: "Sous-titre" },
      { key: "hero.titleLine1", label: "Titre ligne 1" },
      { key: "hero.titleLine2", label: "Titre ligne 2 (doré)" },
      { key: "hero.description", label: "Description" },
    ],
  },
  {
    label: "Sections",
    keys: [
      { key: "fleet.title", label: "Titre Flotte" },
      { key: "fleet.titleHighlight", label: "Flotte (doré)" },
      { key: "activities.title", label: "Titre Activités" },
      { key: "activities.titleHighlight", label: "Activités (doré)" },
      { key: "services.title", label: "Titre Services" },
      { key: "services.titleHighlight", label: "Services (doré)" },
      { key: "destinations.title", label: "Titre Destinations" },
      { key: "destinations.titleHighlight", label: "Destinations (doré)" },
    ],
  },
  {
    label: "Tarifs — Titres des colonnes véhicules",
    keys: [
      { key: "pricing.seats13", label: "Colonne 1 — Titre (défaut : 1-3 places)" },
      { key: "pricing.seats47", label: "Colonne 2 — Titre (défaut : 4-7 places)" },
      { key: "pricing.seats817", label: "Colonne 3 — Titre (défaut : 8-17 places)" },
      { key: "pricing.classeE", label: "Colonne 4 — Mercedes Classe E (titre)" },
      { key: "pricing.classeECustomLabel", label: "Colonne 4 — Libellé affiché (défaut : Sur mesure)" },
    ],
  },
];

const AdminContent = () => {
  const [values, setValues] = useState<Record<string, { value: string; id?: string }>>({});
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    supabase.from("site_content").select("*").eq("lang", "fr").then(({ data }) => {
      const map: Record<string, { value: string; id?: string }> = {};
      data?.forEach((row) => {
        map[row.key] = { value: row.value, id: row.id };
      });
      setValues(map);
    });
  }, []);

  const saveKey = async (key: string) => {
    setSaving(key);
    const entry = values[key];
    if (!entry) { setSaving(null); return; }

    if (entry.id) {
      const { error } = await supabase.from("site_content").update({ value: entry.value }).eq("id", entry.id);
      if (error) toast.error("Erreur: " + error.message);
      else toast.success("Contenu mis à jour !");
    } else {
      const { error, data } = await supabase.from("site_content").insert({ key, lang: "fr", value: entry.value }).select().single();
      if (error) toast.error("Erreur: " + error.message);
      else {
        toast.success("Contenu créé !");
        setValues((prev) => ({ ...prev, [key]: { value: entry.value, id: data.id } }));
      }
    }
    setSaving(null);
  };

  return (
    <div className="space-y-6">
      {CONTENT_GROUPS.map((group) => (
        <Card key={group.label} className="border-border">
          <CardHeader>
            <CardTitle className="font-heading text-lg">{group.label}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {group.keys.map(({ key, label }) => (
              <div key={key} className="flex items-end gap-2">
                <div className="flex-1">
                  <label className="block font-body text-xs text-muted-foreground mb-1">{label}</label>
                  <Input
                    value={values[key]?.value || ""}
                    onChange={(e) => setValues((prev) => ({ ...prev, [key]: { ...prev[key], value: e.target.value } }))}
                    placeholder={key}
                  />
                </div>
                <Button
                  onClick={() => saveKey(key)}
                  disabled={saving === key}
                  size="sm"
                  className="bg-gradient-gold text-primary-foreground shrink-0"
                >
                  <Save className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default AdminContent;
